import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft, FaFileAlt, FaFileUpload, FaTrash } from "react-icons/fa";
import api from "../services/api";
import ToastContext from "../contexts/ToastContext";
import PageHeader from "../components/PageHeader";
import "./EditarMinhaInscricao.css";

const FILE_FIELDS = [
  { key: "COMPROVANTE_RG", label: "Comprovante de RG" },
  { key: "COMPROVANTE_CPF", label: "Comprovante de CPF" },
  { key: "COMPROVANTE_ENDERECO", label: "Comprovante de Endereço" },
  { key: "TEMPO_SERVICO", label: "Comprovante de Tempo de Serviço" },
  { key: "DIPLOMA_GRADUACAO", label: "Diploma de Graduação" },
  { key: "CERTIFICADO_CURSO", label: "Certificado de Cursos" },
];
const REQUIRED_DOCUMENT_TYPES = new Set([
  "COMPROVANTE_RG",
  "COMPROVANTE_CPF",
  "COMPROVANTE_ENDERECO",
]);

const formatInscricaoId = (id) => String(id).padStart(4, "0");

function EditarMinhaInscricao() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useContext(ToastContext);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [detalhe, setDetalhe] = useState(null);
  const [pendingDeleteDoc, setPendingDeleteDoc] = useState(null);
  const [files, setFiles] = useState({
    COMPROVANTE_RG: [],
    COMPROVANTE_CPF: [],
    COMPROVANTE_ENDERECO: [],
    TEMPO_SERVICO: [],
    DIPLOMA_GRADUACAO: [],
    CERTIFICADO_CURSO: [],
  });

  const fetchDetalhe = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/inscricoes/${id}/minha-edicao`);
      setDetalhe(data);
    } catch (error) {
      addToast(error.response?.data?.message || "Erro ao carregar inscrição.", "error");
      navigate("/minhas-inscricoes");
    } finally {
      setLoading(false);
    }
  }, [id, addToast, navigate]);

  useEffect(() => {
    fetchDetalhe();
  }, [fetchDetalhe]);

  const documentosPorTipo = useMemo(() => {
    const grouped = {};
    for (const field of FILE_FIELDS) {
      grouped[field.key] = [];
    }

    for (const doc of detalhe?.documentos || []) {
      const key = doc.tipo_documento;
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(doc);
    }

    return grouped;
  }, [detalhe]);

  const handleInputFiles = (fieldKey, event) => {
    const selected = Array.from(event.target.files || []);
    if (!selected.length) return;

    setFiles((prev) => ({
      ...prev,
      [fieldKey]: [...prev[fieldKey], ...selected],
    }));
    event.target.value = "";
  };

  const removeSelectedFile = (fieldKey, index) => {
    setFiles((prev) => ({
      ...prev,
      [fieldKey]: prev[fieldKey].filter((_, i) => i !== index),
    }));
  };

  const hasFilesToUpload = FILE_FIELDS.some(
    (field) => !REQUIRED_DOCUMENT_TYPES.has(field.key) && files[field.key].length > 0
  );

  const handleSaveDocuments = async () => {
    if (!detalhe?.pode_editar) {
      addToast("Prazo encerrado. Não é possível editar documentos.", "error");
      return;
    }

    if (!hasFilesToUpload) {
      addToast("Selecione ao menos um documento para adicionar.", "error");
      return;
    }

    const formData = new FormData();
    for (const field of FILE_FIELDS) {
      if (REQUIRED_DOCUMENT_TYPES.has(field.key)) continue;
      for (const file of files[field.key]) {
        formData.append(field.key, file);
      }
    }

    try {
      setSaving(true);
      await api.post(`/inscricoes/${id}/documentos`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      addToast("Documentos adicionados com sucesso!", "success");
      setFiles({
        COMPROVANTE_RG: [],
        COMPROVANTE_CPF: [],
        COMPROVANTE_ENDERECO: [],
        TEMPO_SERVICO: [],
        DIPLOMA_GRADUACAO: [],
        CERTIFICADO_CURSO: [],
      });
      await fetchDetalhe();
    } catch (error) {
      addToast(error.response?.data?.message || "Erro ao adicionar documentos.", "error");
    } finally {
      setSaving(false);
    }
  };

  const confirmDeleteDocumento = async () => {
    if (!pendingDeleteDoc) return;

    try {
      await api.delete(`/inscricoes/${id}/documentos/${pendingDeleteDoc.id}`);
      addToast("Documento excluído com sucesso!", "success");
      setPendingDeleteDoc(null);
      await fetchDetalhe();
    } catch (error) {
      addToast(error.response?.data?.message || "Erro ao excluir documento.", "error");
    }
  };

  if (loading) {
    return (
      <>
        <PageHeader title="Editar Inscrição" />
        <p>Carregando dados da inscrição...</p>
      </>
    );
  }

  if (!detalhe) return null;

  return (
    <div className="editar-inscricao-page">
      <PageHeader title="Editar Inscrição" />

      <section className="surface-section edit-info-card">
        <p>
          <strong>Nº Inscrição:</strong> {formatInscricaoId(detalhe.inscricao?.id)}
        </p>
        <p>
          <strong>Cargo inscrito:</strong> {detalhe.cargo?.nome_cargo || "Não informado"}
        </p>
        <p>
          <strong>Edital:</strong> {detalhe.edital?.nome_edital} - {detalhe.edital?.numero_edital}/{detalhe.edital?.ano_edital}
        </p>
        <p>
          <strong>Prazo de edição:</strong>{" "}
          {detalhe.pode_editar
            ? `até ${new Date(detalhe.edital?.data_fim_inscricao).toLocaleString()}`
            : "encerrado"}
        </p>
      </section>

      <section className="surface-section edit-docs-card">
        <h2>Documentos anexados e inclusão de novos</h2>

        {FILE_FIELDS.map((field) => (
          <div key={field.key} className="doc-group">
            <div className="doc-group-header">
              <h3>{field.label}</h3>
              <span
                className={`doc-type-badge ${
                  REQUIRED_DOCUMENT_TYPES.has(field.key)
                    ? "doc-type-badge--required"
                    : "doc-type-badge--optional"
                }`}
              >
                {REQUIRED_DOCUMENT_TYPES.has(field.key)
                  ? "Obrigatório"
                  : "Opcional"}
              </span>
            </div>

            <div className="doc-group-body">
              <div className="doc-subsection">
                <p className="doc-subsection-title">Arquivos anexados</p>
                {(documentosPorTipo[field.key] || []).length > 0 ? (
                  <ul className="doc-list">
                    {documentosPorTipo[field.key].map((doc) => (
                      <li key={doc.id} className="doc-item">
                        <div className="doc-file-chip" title={doc.nome_arquivo}>
                          <FaFileAlt />
                          <span className="doc-file-name">{doc.nome_arquivo}</span>
                        </div>
                        {detalhe.pode_editar && !REQUIRED_DOCUMENT_TYPES.has(field.key) ? (
                          <button
                            type="button"
                            className="ghost-button button-circle doc-delete-btn"
                            onClick={() => setPendingDeleteDoc(doc)}
                            aria-label={`Excluir ${doc.nome_arquivo}`}
                          >
                            <FaTrash />
                          </button>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="muted">Nenhum documento desse tipo.</p>
                )}
              </div>

              <div className="doc-subsection">
                <p className="doc-subsection-title">Adicionar novos arquivos</p>
                {REQUIRED_DOCUMENT_TYPES.has(field.key) ? (
                  <p className="muted small-note">Documento obrigatório: somente visualização.</p>
                ) : detalhe.pode_editar ? (
                  <div className="upload-inline">
                    <label className="btn-modern upload-fancy-button">
                      <FaFileUpload />
                      <span>Adicionar arquivo(s)</span>
                      <input
                        type="file"
                        multiple
                        onChange={(event) => handleInputFiles(field.key, event)}
                        accept=".png,.jpg,.jpeg,.pdf,.doc,.docx,.xls,.xlsx,.odt,.txt"
                        className="visually-hidden"
                      />
                    </label>

                    {files[field.key].length > 0 ? (
                      <ul className="selected-list">
                        {files[field.key].map((file, index) => (
                          <li key={`${field.key}-${file.name}-${index}`}>
                            <span>{file.name}</span>
                            <button
                              type="button"
                              className="ghost-button"
                              onClick={() => removeSelectedFile(field.key, index)}
                            >
                              remover
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="muted small-note">Nenhum novo arquivo selecionado.</p>
                    )}
                  </div>
                ) : (
                  <p className="muted small-note">Prazo encerrado para inclusão de novos arquivos.</p>
                )}
              </div>
            </div>
          </div>
        ))}

        {!detalhe.pode_editar ? (
          <p className="muted">Prazo encerrado. Não é mais possível adicionar ou excluir documentos.</p>
        ) : (
          <div className="edit-footer-actions">
            <div className="edit-actions">
              <button type="button" className="btn-modern" onClick={() => navigate("/minhas-inscricoes")}> 
                <FaArrowLeft />
                <span>Voltar</span>
              </button>
              <button
                type="button"
                className="btn-modern button-secondary"
                disabled={!hasFilesToUpload || saving}
                onClick={handleSaveDocuments}
              >
                <FaFileUpload />
                <span>{saving ? "Salvando..." : "Salvar documentos"}</span>
              </button>
            </div>
          </div>
        )}
      </section>

      {pendingDeleteDoc ? (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal-card surface-section">
            <h3>Confirma exclusão do documento?</h3>
            <p>Se você excluir, este documento sairá da inscrição e você precisará anexar novamente se quiser usar depois.</p>
            <p>
              <strong>Arquivo:</strong> {pendingDeleteDoc.nome_arquivo}
            </p>
            <div className="modal-actions">
              <button type="button" className="btn-modern" onClick={() => setPendingDeleteDoc(null)}>
                <span>Cancelar</span>
              </button>
              <button type="button" className="btn-modern btn-danger" onClick={confirmDeleteDocumento}>
                <span>Confirmar exclusão</span>
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default EditarMinhaInscricao;
